<!-- p-dialog is only the mount (backdrop + focus-trap + body-portal, escaping the
     wizard's transform trap); the shared @u2bUi/dialog chrome renders the panel,
     matching the onboarding tour and the main wizard. `booking-v2-dialog-panel`
     zeroes the PrimeNG content padding and re-enables Tailwind borders. -->
<p-dialog
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [showHeader]="false"
    styleClass="booking-v2-dialog-panel"
    [style]="{ width: '32rem', maxWidth: '95vw' }"
    appendTo="body"
    [visible]="visible()"
    (visibleChange)="visible.set($event)">

    <u2b-dialog class="rounded-[inherit] w-full max-h-[85vh]">

        <div u2bDialogHeader class="contents">
            <h1 class="text-lg font-semibold m-0 text-p-primary-contrast">{{ title() | translate }}</h1>
            <u2b-icon-button
                variant="on-primary"
                icon="close"
                [ariaLabel]="('common.closeDialogAria' | translate)"
                (clicked)="cancel()" />
        </div>

        <div class="flex flex-col gap-4">

            <p class="text-sm text-p-text-muted m-0">{{ assignment().meterFullName }}</p>

            @if (mode() === 'editStart') {

                <!-- Start date -->
                <div class="flex flex-col gap-1">
                    <label for="em-action-start-date" class="text-sm font-medium text-p-text">
                        {{ 'bookingV2.advanced.power.action.startDateLabel' | translate }}
                    </label>
                    <p-datepicker
                        inputId="em-action-start-date"
                        u2bDateMask
                        [u2bDateMaskTime]="true"
                        u2bDatePickerApply
                        #applyStart="u2bDatePickerApply"
                        [pt]="customTimePt"
                        dateFormat="dd.mm.yy"
                        [showTime]="true"
                        hourFormat="24"
                        [showIcon]="true"
                        appendTo="body"
                        styleClass="w-full"
                        [maxDate]="readingDateMax"
                        [placeholder]="('bookingV2.advanced.power.dateTimePlaceholder' | translate)"
                        [ngModel]="startDate()"
                        (ngModelChange)="startDate.set($event)">
                        <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                        <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                        <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                        <ng-template #buttonbar>
                            <u2b-date-picker-button-bar [picker]="applyStart"/>
                        </ng-template>
                    </p-datepicker>
                </div>

                <!-- Start reading -->
                <div class="flex flex-col gap-1">
                    <label for="em-action-start-reading" class="text-sm font-medium text-p-text">
                        {{ 'bookingV2.advanced.power.action.startReadingLabel' | translate }}
                    </label>
                    <p-inputnumber
                        inputId="em-action-start-reading"
                        styleClass="w-full"
                        mode="decimal"
                        [minFractionDigits]="0"
                        [maxFractionDigits]="3"
                        [locale]="currentLocale"
                        [min]="0"
                        [ngModel]="startMeterReading()"
                        (ngModelChange)="startMeterReading.set($event)" />
                </div>

            } @else {

                <!-- End date -->
                <div class="flex flex-col gap-1">
                    <label for="em-action-end-date" class="text-sm font-medium text-p-text">
                        {{ 'bookingV2.advanced.power.action.endDateLabel' | translate }}
                    </label>
                    <p-datepicker
                        inputId="em-action-end-date"
                        u2bDateMask
                        [u2bDateMaskTime]="true"
                        u2bDatePickerApply
                        #applyEnd="u2bDatePickerApply"
                        [pt]="customTimePt"
                        dateFormat="dd.mm.yy"
                        [showTime]="true"
                        hourFormat="24"
                        [showIcon]="true"
                        appendTo="body"
                        styleClass="w-full"
                        [maxDate]="readingDateMax"
                        [disabled]="isApiMeter()"
                        [placeholder]="('bookingV2.advanced.power.dateTimePlaceholder' | translate)"
                        [ngModel]="endDate()"
                        (ngModelChange)="endDate.set($event)">
                        <ng-template #triggericon><span class="material-icons">calendar_today</span></ng-template>
                        <ng-template #previousicon><span class="material-icons">chevron_left</span></ng-template>
                        <ng-template #nexticon><span class="material-icons">chevron_right</span></ng-template>
                        <ng-template #buttonbar>
                            <u2b-date-picker-button-bar [picker]="applyEnd"/>
                        </ng-template>
                    </p-datepicker>
                </div>

                <!-- End reading -->
                <div class="flex flex-col gap-1">
                    <label for="em-action-end-reading" class="text-sm font-medium text-p-text">
                        {{ 'bookingV2.advanced.power.action.endReadingLabel' | translate }}
                    </label>
                    @if (isApiMeter()) {
                        @if (liveReading() !== null) {
                            <div class="flex items-center gap-2 py-2">
                                <span class="font-semibold text-p-text">{{ liveReading() | number:'1.0-2' }} kWh</span>
                                <span class="text-xs text-p-text-muted">{{ 'bookingV2.advanced.power.liveReadingHint' | translate }}</span>
                            </div>
                        } @else {
                            <p class="text-sm text-p-text-muted m-0 py-2">{{ 'bookingV2.advanced.power.action.liveReadingServerSide' | translate }}</p>
                        }
                    } @else {
                        <p-inputnumber
                            inputId="em-action-end-reading"
                            styleClass="w-full"
                            mode="decimal"
                            [minFractionDigits]="0"
                            [maxFractionDigits]="3"
                            [locale]="currentLocale"
                            [min]="assignment().meterReadingStart"
                            [ngModel]="endMeterReading()"
                            (ngModelChange)="endMeterReading.set($event)" />
                    }
                </div>

                <!-- Billing product for usage-billed meters -->
                @if (showProductSelect()) {
                    <div class="flex flex-col gap-1">
                        <label for="em-action-product" class="text-sm font-medium text-p-text">
                            {{ 'bookingV2.advanced.power.productLabel' | translate }}
                        </label>
                        <p-select
                            inputId="em-action-product"
                            styleClass="w-full"
                            appendTo="body"
                            [filter]="true"
                            filterBy="name"
                            optionValue="id"
                            optionLabel="label"
                            [options]="products()"
                            [ngModel]="productId()"
                            (ngModelChange)="productId.set($event)"
                            [placeholder]="('bookingV2.advanced.power.productPlaceholder' | translate)">
                            <ng-template #dropdownicon><span class="material-icons">expand_more</span></ng-template>
                            <ng-template #filtericon><span class="material-icons">search</span></ng-template>
                        </p-select>
                    </div>
                }
            }
        </div>

        <div u2bDialogFooter class="contents">
            <p-button type="button" severity="secondary" [text]="true" [label]="'common.cancel' | translate" (onClick)="cancel()" />
            <p-button type="button" [disabled]="!canSave()" [label]="'common.save' | translate" (onClick)="save()" />
        </div>
    </u2b-dialog>
</p-dialog>
